import { useState, useEffect } from "react";
import { X, Search as SearchIcon, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

interface TagOption {
  id: string;
  label: string;
}

interface TagCategory {
  id: string;
  label: string;
  tags: TagOption[];
  experienceTypes: ('dine-in' | 'takeout' | 'delivery')[];
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: { include: string[]; exclude: string[] }) => void;
  initialFilters?: { include: string[]; exclude: string[] };
  selectedExperienceTypes: ('dine-in' | 'takeout' | 'delivery')[];
  onExperienceTypeToggle?: (type: 'dine-in' | 'takeout' | 'delivery') => void;
}

const experienceOptions = [
  { id: 'dine-in' as const, label: 'Dine Out' },
  { id: 'takeout' as const, label: 'Takeout' },
  { id: 'delivery' as const, label: 'Delivery' }
];

// Tag categories based on the outline
const tagCategories: TagCategory[] = [
  {
    id: 'highlights',
    label: 'Highlights',
    experienceTypes: ['dine-in', 'takeout', 'delivery'],
    tags: [
      { id: 'the-best', label: 'The Best' },
      { id: 'elite-reviewers', label: 'Elite Reviewers' },
      { id: 'trusted-foodie-picks', label: 'Trusted Foodie Picks' },
      { id: 'top-rated-9plus', label: 'Top Rated (9+)' },
      { id: 'friends-recommend', label: 'Friends Recommend' },
      { id: 'following', label: 'Following' },
      { id: 'high-trust-score', label: 'High Trust Score' },
      { id: 'trending-now', label: 'Trending Now' },
    ]
  },
  {
    id: 'cuisine',
    label: 'Cuisine',
    experienceTypes: ['dine-in', 'takeout', 'delivery'],
    tags: [
      { id: 'italian', label: 'Italian' },
      { id: 'mexican', label: 'Mexican' },
      { id: 'asian', label: 'Asian' },
      { id: 'american', label: 'American' },
      { id: 'seafood', label: 'Seafood' },
      { id: 'vegetarian', label: 'Vegetarian' },
      { id: 'vegan', label: 'Vegan' },
      { id: 'thai', label: 'Thai' },
      { id: 'indian', label: 'Indian' },
      { id: 'chinese', label: 'Chinese' },
      { id: 'mediterranean', label: 'Mediterranean' },
      { id: 'bbq', label: 'BBQ' },
      { id: 'fusion', label: 'Fusion' },
      { id: 'southern', label: 'Southern' },
      { id: 'japanese', label: 'Japanese' },
    ]
  },
  {
    id: 'ambiance',
    label: 'Ambiance',
    experienceTypes: ['dine-in'],
    tags: [
      { id: 'romantic', label: 'Romantic' },
      { id: 'casual', label: 'Casual' },
      { id: 'upscale', label: 'Upscale' },
      { id: 'noisy', label: 'Noisy' },
      { id: 'quiet', label: 'Quiet' },
      { id: 'cozy', label: 'Cozy' },
      { id: 'outdoor-seating', label: 'Outdoor Seating' },
      { id: 'family-friendly', label: 'Family-Friendly' },
      { id: 'good-for-kids', label: 'Good for Kids' },
      { id: 'pet-friendly', label: 'Pet-Friendly' },
      { id: 'trendy', label: 'Trendy' },
      { id: 'historic', label: 'Historic' },
      { id: 'vibrant', label: 'Vibrant' },
      { id: 'intimate', label: 'Intimate' },
      { id: 'spacious', label: 'Spacious' },
      { id: 'hidden-gem', label: 'Hidden Gem' },
      { id: 'must-try', label: 'Must-Try' },
    ]
  },
  {
    id: 'service',
    label: 'Service',
    experienceTypes: ['dine-in'],
    tags: [
      { id: 'attentive', label: 'Attentive' },
      { id: 'friendly-staff', label: 'Friendly Staff' },
      { id: 'slow-service', label: 'Slow Service' },
      { id: 'efficient', label: 'Efficient' },
      { id: 'knowledgeable', label: 'Knowledgeable' },
      { id: 'rude', label: 'Rude' },
      { id: 'personalized', label: 'Personalized' },
      { id: 'overpriced-service', label: 'Overpriced Service' },
      { id: 'helpful', label: 'Helpful' },
      { id: 'inattentive', label: 'Inattentive' },
      { id: 'professional', label: 'Professional' },
      { id: 'welcoming', label: 'Welcoming' },
      { id: 'slow-wait', label: 'Slow Wait' },
      { id: 'overcrowded', label: 'Overcrowded' },
      { id: 'prompt', label: 'Prompt' },
    ]
  },
  {
    id: 'value-price',
    label: 'Value / Price',
    experienceTypes: ['dine-in', 'takeout', 'delivery'],
    tags: [
      { id: 'great-value', label: 'Great Value' },
      { id: 'expensive', label: 'Expensive' },
      { id: 'cheap-eats', label: 'Cheap Eats' },
      { id: 'mid-range', label: 'Mid-Range' },
      { id: 'overpriced', label: 'Overpriced' },
      { id: 'budget-friendly', label: 'Budget-Friendly' },
      { id: 'worth-it', label: 'Worth It' },
      { id: 'pricey', label: 'Pricey' },
      { id: 'affordable', label: 'Affordable' },
      { id: 'good-portions', label: 'Good Portions' },
      { id: 'fine-dining', label: 'Fine Dining' },
      { id: 'value-meal', label: 'Value Meal' },
      { id: 'hidden-gem', label: 'Hidden Gem' },
      { id: 'splurge-worthy', label: 'Splurge-Worthy' },
      { id: 'fair-pricing', label: 'Fair Pricing' },
      { id: 'low-fees', label: 'Low Fees' },
      { id: 'high-fees', label: 'High Fees' },
      { id: 'worth-wait', label: 'Worth the Wait' },
      { id: 'overpriced-delivery', label: 'Overpriced Delivery' },
      { id: 'budget-option', label: 'Budget Option' },
      { id: 'deal-worthy', label: 'Deal Worthy' },
      { id: 'extra-charges', label: 'Extra Charges' },
      { id: 'transparent-pricing', label: 'Transparent Pricing' },
      { id: 'value-for-money', label: 'Value for Money' },
      { id: 'promo-friendly', label: 'Promo-Friendly' },
      { id: 'fee-heavy', label: 'Fee-Heavy' },
      { id: 'affordable-addons', label: 'Affordable Add-Ons' },
      { id: 'hidden-costs', label: 'Hidden Costs' },
      { id: 'good-deal', label: 'Good Deal' },
      { id: 'price-match', label: 'Price Match' },
    ]
  },
  {
    id: 'dietary',
    label: 'Dietary',
    experienceTypes: ['dine-in', 'takeout', 'delivery'],
    tags: [
      { id: 'gluten-free', label: 'Gluten-Free' },
      { id: 'vegetarian-options', label: 'Vegetarian Options' },
      { id: 'vegan-options', label: 'Vegan' },
      { id: 'nut-free', label: 'Nut-Free' },
      { id: 'dairy-free', label: 'Dairy-Free' },
      { id: 'healthy', label: 'Healthy' },
      { id: 'low-carb', label: 'Low-Carb' },
      { id: 'keto', label: 'Keto' },
      { id: 'halal', label: 'Halal' },
      { id: 'kosher', label: 'Kosher' },
      { id: 'organic', label: 'Organic' },
      { id: 'allergy-friendly', label: 'Allergy-Friendly' },
      { id: 'plant-based', label: 'Plant-Based' },
      { id: 'low-sodium', label: 'Low-Sodium' },
      { id: 'sustainable', label: 'Sustainable' },
    ]
  },
  {
    id: 'packaging',
    label: 'Packaging',
    experienceTypes: ['takeout', 'delivery'],
    tags: [
      { id: 'leak-proof', label: 'Leak-Proof' },
      { id: 'well-insulated', label: 'Well-Insulated' },
      { id: 'sturdy', label: 'Sturdy' },
      { id: 'eco-friendly', label: 'Eco-Friendly' },
      { id: 'poor-packaging', label: 'Poor Packaging' },
      { id: 'no-utensils', label: 'No Utensils' },
      { id: 'overpacked', label: 'Overpacked' },
      { id: 'minimal-waste', label: 'Minimal Waste' },
      { id: 'secure', label: 'Secure' },
      { id: 'damaged-easily', label: 'Damaged Easily' },
      { id: 'reusable', label: 'Reusable' },
      { id: 'compact', label: 'Compact' },
      { id: 'temperature-controlled', label: 'Temperature-Controlled' },
      { id: 'fresh-on-arrival', label: 'Fresh on Arrival' },
      { id: 'no-leaks', label: 'No Leaks' },
    ]
  },
  {
    id: 'delivery-speed',
    label: 'Speed',
    experienceTypes: ['takeout', 'delivery'],
    tags: [
      { id: 'fast-delivery', label: 'Fast Delivery' },
      { id: 'on-time', label: 'On-Time' },
      { id: 'late-arrival', label: 'Late Arrival' },
      { id: 'quick-prep', label: 'Quick Prep' },
      { id: 'prompt-delivery', label: 'Prompt' },
      { id: 'delayed', label: 'Delayed' },
      { id: 'efficient-delivery', label: 'Efficient' },
      { id: 'slow-delivery', label: 'Slow' },
      { id: 'reliable-timing', label: 'Reliable Timing' },
      { id: 'under-30-min', label: 'Under 30 Min' },
      { id: 'over-hour', label: 'Over an Hour' },
      { id: 'scheduled-accurately', label: 'Scheduled Accurately' },
      { id: 'traffic-proof', label: 'Traffic-Proof' },
      { id: 'weekend-rush', label: 'Weekend Rush' },
      { id: 'peak-hour-friendly', label: 'Peak Hour Friendly' },
    ]
  },
  {
    id: 'food-quality',
    label: 'Food Quality',
    experienceTypes: ['takeout', 'delivery'],
    tags: [
      { id: 'hot-fresh', label: 'Hot & Fresh' },
      { id: 'still-warm', label: 'Still Warm' },
      { id: 'accurate-order', label: 'Accurate Order' },
      { id: 'cold-food', label: 'Cold Food' },
      { id: 'missing-items', label: 'Missing Items' },
      { id: 'soggy', label: 'Soggy' },
      { id: 'overcooked', label: 'Overcooked' },
      { id: 'fresh-tasting', label: 'Fresh Tasting' },
      { id: 'undercooked', label: 'Undercooked' },
      { id: 'complete-order', label: 'Complete Order' },
      { id: 'flavor-preserved', label: 'Flavor Preserved' },
      { id: 'sauces-intact', label: 'Sauces Intact' },
      { id: 'portions-correct', label: 'Portions Correct' },
      { id: 'travel-worthy', label: 'Travel-Worthy' },
      { id: 'quality-held-up', label: 'Quality Held Up' },
    ]
  },
];

type TagState = 'neutral' | 'include' | 'exclude';

export default function FilterModal({ 
  isOpen, 
  onClose, 
  onApplyFilters,
  initialFilters = { include: [], exclude: [] },
  selectedExperienceTypes,
  onExperienceTypeToggle
}: FilterModalProps) {
  const [tagStates, setTagStates] = useState<{ [key: string]: TagState }>({});
  const [searchQueries, setSearchQueries] = useState<{ [categoryId: string]: string }>({});

  // Sync tag states when modal opens or initialFilters change
  useEffect(() => {
    if (isOpen) {
      const newStates: { [key: string]: TagState } = {};
      initialFilters.include.forEach(tag => newStates[tag] = 'include');
      initialFilters.exclude.forEach(tag => newStates[tag] = 'exclude');
      setTagStates(newStates);
    }
  }, [isOpen, initialFilters]);

  const toggleTag = (tagId: string) => {
    setTagStates(prev => {
      const currentState = prev[tagId] || 'neutral';
      let newState: TagState;
      
      // Cycle: neutral -> include -> exclude -> neutral
      if (currentState === 'neutral') {
        newState = 'include';
      } else if (currentState === 'include') {
        newState = 'exclude';
      } else {
        newState = 'neutral';
      }
      
      const newStates = { ...prev };
      if (newState === 'neutral') {
        delete newStates[tagId];
      } else {
        newStates[tagId] = newState;
      }
      
      return newStates;
    });
  };

  const clearAll = () => {
    setTagStates({});
  };

  const applyFilters = () => {
    const include: string[] = [];
    const exclude: string[] = [];
    
    Object.entries(tagStates).forEach(([tagId, state]) => {
      if (state === 'include') include.push(tagId);
      if (state === 'exclude') exclude.push(tagId);
    });
    
    onApplyFilters({ include, exclude });
    onClose();
  };

  const getFilteredTags = (category: TagCategory) => {
    const query = searchQueries[category.id]?.toLowerCase() || '';
    if (!query) return category.tags;
    return category.tags.filter(tag => tag.label.toLowerCase().includes(query));
  };

  const getTagBadgeClasses = (state: TagState) => {
    if (state === 'include') {
      return "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700";
    } else if (state === 'exclude') {
      return "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700";
    }
    return "";
  };

  const getSelectedCount = () => {
    return Object.keys(tagStates).length;
  };

  const getIncludeCount = () => {
    return Object.values(tagStates).filter(s => s === 'include').length;
  };

  const getExcludeCount = () => {
    return Object.values(tagStates).filter(s => s === 'exclude').length;
  };

  // Filter categories based on selected experience types
  const relevantCategories = tagCategories.filter(category => 
    category.experienceTypes.some(type => selectedExperienceTypes?.includes(type))
  );

  // Get all selected tags with their details for display
  const getSelectedTags = () => {
    const selected: { id: string; label: string; state: TagState }[] = [];
    Object.entries(tagStates).forEach(([tagId, state]) => {
      // Find the tag label
      for (const category of tagCategories) {
        const tag = category.tags.find(t => t.id === tagId);
        if (tag) {
          selected.push({ id: tagId, label: tag.label, state });
          break;
        }
      }
    });
    return selected;
  };

  const selectedTags = getSelectedTags();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg mx-auto max-h-[90svh] flex flex-col" data-testid="filter-modal">
        <DialogHeader className="pb-4 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold" data-testid="filter-modal-title">
            Filter Reviews
          </DialogTitle>
          {getSelectedCount() > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              {getIncludeCount() > 0 && <span className="text-green-700 dark:text-green-400">{getIncludeCount()} Must Have</span>}
              {getIncludeCount() > 0 && getExcludeCount() > 0 && <span> • </span>}
              {getExcludeCount() > 0 && <span className="text-red-700 dark:text-red-400">{getExcludeCount()} Avoid</span>}
            </p>
          )}
        </DialogHeader>

        {/* Experience Type Selection */}
        {onExperienceTypeToggle && (
          <div className="flex-shrink-0 pb-4 border-b border-border">
            <h3 className="text-sm font-medium mb-3">Show reviews for</h3>
            <div className="flex gap-2">
              {experienceOptions.map((option) => (
                <Button
                  key={option.id}
                  variant="outline"
                  size="sm"
                  onClick={() => onExperienceTypeToggle(option.id)}
                  className={cn(
                    "flex-1 h-10 rounded-lg transition-all",
                    selectedExperienceTypes.includes(option.id)
                      ? "bg-primary/10 text-primary border-primary/30"
                      : "bg-background text-muted-foreground border-border"
                  )}
                  data-testid={`filter-experience-${option.id}`}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {selectedTags.length > 0 && (
          <div className="flex-shrink-0 pb-4 border-b border-border">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">Selected Filters</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="h-7 text-xs text-muted-foreground"
                data-testid="button-clear-all-top"
              >
                Clear All
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedTags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant="default"
                  className={cn(
                    "cursor-pointer text-sm py-1.5 px-3",
                    getTagBadgeClasses(tag.state)
                  )}
                  onClick={() => toggleTag(tag.id)}
                  data-testid={`selected-tag-${tag.id}`}
                >
                  {tag.state === 'include' && <span className="mr-1.5 font-bold">✓</span>}
                  {tag.state === 'exclude' && <span className="mr-1.5 font-bold">✗</span>}
                  {tag.label}
                  <X className="ml-1.5 w-3 h-3" />
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="text-xs text-muted-foreground mb-3 flex-shrink-0">
          Tap once for <span className="text-green-700 dark:text-green-400 font-medium">Must Have</span>, twice for <span className="text-red-700 dark:text-red-400 font-medium">Avoid</span>, third tap to remove
        </div>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          <Accordion type="multiple" className="w-full">
            {relevantCategories.map((category) => {
              const filteredTags = getFilteredTags(category);
              const selectedInCategory = category.tags.filter(tag => tagStates[tag.id]).length;
              const isTheBest = category.id === 'the-best';
              
              return (
                <AccordionItem key={category.id} value={category.id}>
                  <AccordionTrigger className="hover:no-underline" data-testid={`accordion-${category.id}`}>
                    <div className="flex items-center justify-between w-full pr-2">
                      <div className="flex items-center gap-2">
                        {isTheBest && <Sparkles className="w-4 h-4 text-primary" />}
                        <span className="font-medium">{category.label}</span>
                      </div>
                      {selectedInCategory > 0 && (
                        <Badge 
                          variant="secondary" 
                          className="ml-2 h-5 px-2 text-xs"
                          data-testid={`category-badge-${category.id}`}
                        >
                          {selectedInCategory}
                        </Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pt-2">
                      {/* Search within category - only show for Cuisine */}
                      {category.id === 'cuisine' && (
                        <div className="relative">
                          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="text"
                            placeholder={`Search ${category.label.toLowerCase()}...`}
                            value={searchQueries[category.id] || ''}
                            onChange={(e) => setSearchQueries(prev => ({
                              ...prev,
                              [category.id]: e.target.value
                            }))}
                            className="pl-9 h-9"
                            data-testid={`search-${category.id}`}
                          />
                        </div>
                      )}
                      
                      {/* Tags */}
                      <div className="flex flex-wrap gap-2">
                        {filteredTags.map((tag) => {
                          const state = tagStates[tag.id] || 'neutral';
                          return (
                            <Badge
                              key={tag.id}
                              variant={state === 'neutral' ? 'outline' : 'default'}
                              className={cn(
                                "cursor-pointer text-sm py-2 px-3 min-h-[48px] flex items-center justify-center transition-all",
                                getTagBadgeClasses(state)
                              )}
                              onClick={() => toggleTag(tag.id)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleTag(tag.id);
                                }
                              }}
                              data-testid={`tag-${tag.id}`}
                            >
                              {state === 'include' && <span className="mr-1.5 font-bold">✓</span>}
                              {state === 'exclude' && <span className="mr-1.5 font-bold">✗</span>}
                              {tag.label}
                            </Badge>
                          );
                        })}
                      </div>
                      
                      {filteredTags.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No tags found
                        </p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div className="pt-4 border-t border-border flex-shrink-0">
          <Button
            onClick={applyFilters}
            className="w-full"
            data-testid="button-apply-filters"
          >
            Apply {getSelectedCount() > 0 && `(${getSelectedCount()})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
